


import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Box,
  Typography,
  IconButton
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { createPoll, updatePoll } from "../../../services/adminService";
import {
  getCountries,
  getStatesByCountry,
  getCitiesByState,
  getCategories,
  getSubcategories,
} from '../../../services/apiService';

const PollForm = ({ pollData, onSuccess }) => {
  const [question, setQuestion] = useState(pollData?.question || "");
  const [options, setOptions] = useState(pollData?.options?.map(opt => opt.text) || ["", ""]);
  const [startDate, setStartDate] = useState(pollData?.start_date?.slice(0, 16) || "");
  const [endDate, setEndDate] = useState(pollData?.end_date?.slice(0, 16) || "");
  const [visibility, setVisibility] = useState(pollData?.visibility || "public");

  // Country/State/City
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [country, setCountry] = useState(pollData?.country?._id || "");
  const [state, setState] = useState(pollData?.state?._id || "");
  const [city, setCity] = useState(pollData?.city?._id || "");

  // Category/Subcategory
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [category, setCategory] = useState(pollData?.category?._id || "");
  const [subCategory, setSubCategory] = useState(pollData?.subCategory?._id || "");

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCountries();
    loadCategories();
  }, []);

  useEffect(() => {
    if (country) loadStates(country);
  }, [country]);

  useEffect(() => {
    if (state) loadCities(state);
  }, [state]);

  useEffect(() => {
    if (category) loadSubcategories(category);
  }, [category]);

  const loadCountries = async () => {
    try {
      const res = await getCountries();
      setCountries(res.data);
    } catch (error) {
      console.error("Error loading countries:", error);
    }
  };

  const loadStates = async (countryId) => {
    try {
      const res = await getStatesByCountry(countryId);
      setStates(res.data);
    } catch (error) {
      console.error("Error loading states:", error);
      setStates([]);
    }
  };

  const loadCities = async (stateId) => {
    try {
      const res = await getCitiesByState(stateId);
      setCities(res.data);
    } catch (error) {
      console.error("Error loading cities:", error);
      setCities([]);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await getCategories();
      setCategories(res.data);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const loadSubcategories = async (categoryId) => {
    try {
      const res = await getSubcategories(categoryId);
      setSubcategories(res.data);
    } catch (error) {
      console.error("Error loading subcategories:", error);
      setSubcategories([]);
    }
  };

  // Options logic
  const addOption = () => setOptions([...options, ""]);
  const removeOption = (index) => setOptions(options.filter((_, i) => i !== index));
  const handleOptionChange = (index, value) => {
    const updated = [...options];
    updated[index] = value;
    setOptions(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = {
        question,
        options: options.filter(opt => opt.trim() !== ""),
        start_date: startDate,
        end_date: endDate,
        visibility,
        country,
        state,
        city,
        category,
        subCategory
      };

      if (pollData?._id) {
        await updatePoll(pollData._id, formData);
      } else {
        await createPoll(formData);
      }

      onSuccess?.();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxWidth="600px" mx="auto" mt={4} p={3} border="1px solid #ccc" borderRadius={2}>
      <Typography variant="h5" mb={2}>
        {pollData?._id ? "Edit Poll" : "Create Poll"}
      </Typography>

      <form onSubmit={handleSubmit}>
        <TextField
          label="Question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          fullWidth
          required
          margin="normal"
        />

        <Typography mt={2}>Options</Typography>
        {options.map((opt, idx) => (
          <Box key={idx} display="flex" alignItems="center" mt={1}>
            <TextField
              label={`Option ${idx + 1}`}
              value={opt}
              onChange={(e) => handleOptionChange(idx, e.target.value)}
              fullWidth
              required
            />
            <IconButton onClick={() => removeOption(idx)} disabled={options.length <= 2}>
              <Remove />
            </IconButton>
          </Box>
        ))}
        <Button startIcon={<Add />} onClick={addOption} sx={{ mt: 1 }}>
          Add Option
        </Button>

        <TextField
          label="Start Date & Time"
          type="datetime-local"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          fullWidth
          required
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date & Time"
          type="datetime-local"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          fullWidth
          required
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Visibility"
          select
          value={visibility}
          onChange={(e) => setVisibility(e.target.value)}
          fullWidth
          margin="normal"
        >
          <MenuItem value="public">Public</MenuItem>
          <MenuItem value="registered">Registered Users</MenuItem>
        </TextField>

        <TextField
          label="Category"
          select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          fullWidth
          margin="normal"
        >
          {categories.map(cat => (
            <MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>
          ))}
        </TextField>

        <TextField
          label="Subcategory"
          select
          value={subCategory}
          onChange={(e) => setSubCategory(e.target.value)}
          fullWidth
          margin="normal"
        >
          {subcategories.map(sub => (
            <MenuItem key={sub._id} value={sub._id}>{sub.name}</MenuItem>
          ))}
        </TextField>

        <TextField
          label="Country"
          select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          fullWidth
          margin="normal"
        >
          {countries.map(c => (
            <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
          ))}
        </TextField>

        <TextField
          label="State"
          select
          value={state}
          onChange={(e) => setState(e.target.value)}
          fullWidth
          margin="normal"
        >
          {states.map(s => (
            <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
          ))}
        </TextField>

        <TextField
          label="City"
          select
          value={city}
          onChange={(e) => setCity(e.target.value)}
          fullWidth
          margin="normal"
        >
          {cities.map(c => (
            <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
          ))}
        </TextField>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 3 }}
          disabled={loading}
        >
          {loading ? "Saving..." : pollData?._id ? "Update Poll" : "Create Poll"}
        </Button>
      </form>
    </Box>
  );
};

export default PollForm;
